import LeftMenu from '@/pages/home/components/LeftMenu';
import { LOCALE_EN } from '@gp/_config/config';
import PageAccess from '@gp/components/PageAccess';
import { G_POP_SYSTEM } from '@gp/constants/const';
import { DESCRIPTION_A } from '@gp/constants/noPermissionTips';
import { SIDE_MENU_CODE_MAP } from '@gp/constants/sideMenus';
import {
  GPOPLeftMenuModelState,
  LEFT_MENU_NAMESPACE,
} from '@gp/models/gpopLeftMenuList';
import { SYSTEM_GUIDE_MODEL_NAMESPACE } from '@gp/models/systemGuide';
import { TIPS_VIEW_MODEL_NAMESPACE } from '@gp/models/tipsView';
import { queryGuideInfo } from '@gp/services/global';
import { dispatchResizeEvent } from '@gp/utils/common';
import {
  FILTER_SAVE_VALUE_KEY,
  FILTER_STORAGE_VALUE_KEY,
} from '@gp/utils/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import VersionAutoPopup from '@home/components/VersionPopup/VersionAutoPopup';
import { useRequest } from 'ahooks';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ConfigProvider, Layout, StatusTip } from 'tea-component';
import { useDispatch, useSelector } from 'umi';
import './global.scss';
import './index.scss';
import type { MenuResultType, TransfrormMenuType } from './types';
import GPCustomMenu from '@gp/components/GPCustomMenu';

const { Body, Content } = Layout;
const locale = LANGUAGE_EN;

(window as any).isInIframe = /* window.top != window.self */ false;

// 非开发环境和嵌入式下，globalpedia-pc 重定向到globalpedia
if (
  process.env.NODE_ENV !== 'development' &&
  !(window as any).isInIframe &&
  window.location.pathname.startsWith('/globalpedia-pc')
) {
  window.location.replace(
    window.location.href.replace('/globalpedia-pc', '/globalpedia'),
  );
}

// 用于鼠标划入划出菜单栏时，小屏幕情况下将横向滚动条复位滚动到0
const pageXOffsetZero = () => {
  const timer = setTimeout(() => {
    window.pageXOffset = 0;
    window.scrollX = 0;
    document.documentElement.scrollLeft = 0;
    clearTimeout(timer);
  }, 0);
};

// GPOP 2024-07-31之前的公告 即使未查看，也不自动弹出
const TIME = '2024-07-31 00:00:00';
const getCanAutoPopup = (
  sysEnv: number,
  sysType: number,
  versionNotice: Record<string, any>,
): boolean => {
  const { release_time: releaseTime } = versionNotice || {};
  const releaseTimeM = !releaseTime ? null : moment(releaseTime);
  return !!releaseTimeM?.isValid() && releaseTimeM.isSameOrAfter(moment(TIME));
};

const getMenuPath = (path?: string, route?: any): string => {
  const tempPath = path || route?.to || '';
  return tempPath?.startsWith('/') ? tempPath?.substring(1) : tempPath;
};

export default function Layouts(props: any) {
  moment.locale(locale === 'en-US' ? ['en', 'en-US'] : ['zh-cn']);
  const leftMenuCtx = useRef<HTMLDivElement>(null);
  const [leftMenuWidthSize, setLeftMenuWidthSize] = useState<number>(248);
  const [hoverChange, setHoverChange] = useState<number>(0);
  const [outerChange, setOuterChange] = useState<number>(0);

  const state = useSelector((state: any) => state);
  const { authSet, menuList, isFoldMenu }: GPOPLeftMenuModelState =
    state?.[LEFT_MENU_NAMESPACE];
  const loading = state?.loading?.effects?.[`${LEFT_MENU_NAMESPACE}/query`];

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({ type: `${LEFT_MENU_NAMESPACE}/query` });
    dispatch({
      type: `${TIPS_VIEW_MODEL_NAMESPACE}/getTipsViewState`,
      payload: [],
    });
  }, []);

  const { run: guideInfoRun } = useRequest(queryGuideInfo, {
    manual: true,
    onSuccess: (res: boolean) => {
      dispatch({
        type: `${SYSTEM_GUIDE_MODEL_NAMESPACE}/updateSystemEntryGudie`,
        payload: res,
      });
    },
  });
  const sideRoutes: TransfrormMenuType = useMemo(() => {
    if (!Array.isArray(menuList)) return [];
    const getSecondMenu = (list: MenuResultType[]) =>
      list.map((item) => {
        const { id, page_id, name_en, assembly_url } = item;
        const defaultRoute = SIDE_MENU_CODE_MAP[page_id] || {};
        const tempPath = assembly_url || defaultRoute.to || '';
        const path =
          (typeof tempPath === 'string' && tempPath?.[0] === '/'
            ? tempPath?.slice(1)
            : tempPath) || '';
        return {
          id,
          className: (name_en || defaultRoute.title)?.replaceAll(' ', '-'),
          title: name_en || defaultRoute.title,
          path,
          env: ['pre', 'pro'],
          ...item,
          showNew: !!defaultRoute?.showNew,
          newMarkType: defaultRoute?.newMarkType,
          type: defaultRoute?.type || '',
        };
      });

    // const menuListNew = [...menuList, ...newMenuList];

    return menuList?.map((item: any) => {
      const { id, page_id, name_en, sub_menus, assembly_url } = item;
      const defaultRoute = SIDE_MENU_CODE_MAP[page_id] || {};
      return {
        id,
        className: (name_en || defaultRoute.title)?.replaceAll(' ', '-'),
        title: (
          <div className="menu-item">
            <div className="menu-item-text font-bold">
              {name_en || defaultRoute.title}
            </div>
          </div>
        ),
        defaultOpened: defaultRoute?.defaultOpened,
        subMenus: sub_menus ? getSecondMenu(sub_menus) : undefined,
        env: ['pre', 'pro'],
        ...item,
        icon: defaultRoute.icon,
        path: getMenuPath(assembly_url, defaultRoute),
        showNew: !!defaultRoute?.showNew,
        newMarkType: defaultRoute?.newMarkType,
        type: defaultRoute?.type || '',
      };
    });
  }, [menuList]);

  const noAuth = useMemo(() => {
    return authSet.size === 0;
  }, [authSet]);
  const errorInfo = useMemo(() => {
    if (loading)
      return (
        <StatusTip
          status="loading"
          loadingText="Loading..."
          style={{ margin: 'auto' }}
        />
      );
    if (noAuth)
      return (
        <PageAccess isAccess={false} description={DESCRIPTION_A}></PageAccess>
      );
    return undefined;
  }, [loading, noAuth]);

  const handleMouseMoveLeftMenu = useCallback(
    (e: any) => {
      if (
        ['tea-icon tea-icon-arrowleft', 'menu-list-fold-btn']?.includes(
          e?.target?.getAttribute('class'),
        )
      ) {
        setOuterChange(Date.now());
        return;
      }
      setHoverChange(Date.now());
      if (isFoldMenu) {
        // 通知window触发resize事件，解决echarts不刷新导致的ui问题
        dispatchResizeEvent();
      }
    },
    [isFoldMenu],
  );

  const handleMouseOutLeftMenu = useCallback(() => {
    setOuterChange(Date.now());
    if (isFoldMenu) {
      // 通知window触发resize事件，解决echarts不刷新导致的ui问题
      dispatchResizeEvent();
      // 鼠标在底部移到时，添加和移除的类名还原回来
      const ele = document.querySelector('.gp-left__menu .tea-menu');
      const el = document.querySelector(
        '.gp-left__menu .tea-menu__body .is-selected',
      );
      if (ele) {
        ele.classList.add('tea-menu-is-collapsed');
      }
      if (el) {
        el?.classList.remove('temp-is-no-selected');
      }
    }
  }, [isFoldMenu]);

  const removeEventListener = useCallback(() => {
    leftMenuCtx?.current?.removeEventListener(
      'mouseover',
      handleMouseMoveLeftMenu,
    );
    leftMenuCtx?.current?.removeEventListener(
      'mouseleave',
      handleMouseOutLeftMenu,
    );
  }, [handleMouseMoveLeftMenu, handleMouseOutLeftMenu]);

  useEffect(() => {
    guideInfoRun({ page: G_POP_SYSTEM });
    const timer = setInterval(() => {
      const ele = document.querySelector('.gp-left__menu');
      if (ele) {
        clearInterval(timer);
        (leftMenuCtx as any).current = ele;
        ele?.addEventListener('mouseover', handleMouseMoveLeftMenu);
        ele?.addEventListener('mouseleave', handleMouseOutLeftMenu);
      }
    }, 300);

    return () => {
      removeEventListener();
    };
  }, [handleMouseMoveLeftMenu, handleMouseOutLeftMenu, removeEventListener]);

  useEffect(() => {
    setLeftMenuWidthSize(isFoldMenu ? 56 : 248);
    setTimeout(() => {
      // 通知window触发resize事件，解决echarts不刷新导致的ui问题
      dispatchResizeEvent();
    });
  }, [isFoldMenu]);

  useEffect(() => {
    pageXOffsetZero();
    if (!hoverChange) return;
    if (leftMenuWidthSize < 248) {
      setLeftMenuWidthSize(248);
    }
  }, [hoverChange]);

  useEffect(() => {
    pageXOffsetZero();
    if (!outerChange) return;
    if (isFoldMenu) {
      // 折叠的状态
      setLeftMenuWidthSize(56);
    }
  }, [outerChange]);

  useEffect(() => {
    // 退出清空缓存
    window.sessionStorage.removeItem(FILTER_STORAGE_VALUE_KEY);
    window.sessionStorage.removeItem(FILTER_SAVE_VALUE_KEY);
  }, []);

  return (
    <ConfigProvider locale={LOCALE_EN}>
      <Body className="layout__body dbg-layout-body">
        <LeftMenu
          title="G-POP"
          titleCN="G-POP"
          menuList={sideRoutes}
          isHyperlink
          children={<GPCustomMenu menuList={sideRoutes} isHyperlink />}
        />
        <Content
          className={`dbg-body ${locale === 'en-US' ? 'dbg-en' : 'dbg-zh'}`}
        >
          {errorInfo || props.children}
          <VersionAutoPopup getCanAutoPopup={getCanAutoPopup} />
        </Content>
      </Body>
    </ConfigProvider>
  );
}
